import { PageHeader, PageShell } from '@/components/PageShell';
import { DocumentStatusBadge } from '@/components/badges/DocumentStatusBadge';
import { type SpaceFilter, STATUS_FILTER_LABELS, type StatusFilter } from '@/components/filters/documentStatus';
import { useDocumentListFilters } from '@/components/filters/useDocumentListFilters';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAppliesToActions, useIsDraftPending } from '@/db/useAppliesToActions';
import { useRowClick } from '@/lib/navigation';
import { useSetSearchParam } from '@/lib/searchParams';
import {
    considerationQueryFlagsState,
    considerationTypesState,
    spacesState,
    taskTypesByConsiderationTypeState,
} from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { type ConsiderationType, DocumentStatusSchema } from '@rock/types';
import { orderBy } from 'lodash';
import { type FC, type MouseEvent, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { QueryStatus } from './applies-to/QueryStatus';
import { isQueryFlag, QUERY_FLAG_LABELS, QUERY_FLAGS, type QueryFlag, typesMissingQueries } from './appliesToStatus';

export const ConsiderationTypesPage: FC = () => {
    const considerationTypes = useAsyncAtomValue(considerationTypesState, []);
    const spaces = useAsyncAtomValue(spacesState, []);
    const queryFlags = useAsyncAtomValue(considerationQueryFlagsState);
    const taskTypesByType = useAsyncAtomValue(taskTypesByConsiderationTypeState);
    const onRowClick = useRowClick();
    const { busy, draftMissing } = useAppliesToActions();
    const isDraftPending = useIsDraftPending();
    const [searchParams] = useSearchParams();
    const setSearchParam = useSetSearchParam();
    const queryParam = searchParams.get('query');
    const queryFilter: QueryFlag | 'all' = isQueryFlag(queryParam) ? queryParam : 'all';
    const { statusFilter, spaceFilter, setStatusFilter, setSpaceFilter } = useDocumentListFilters();

    const sortedSpaces = useMemo(() => orderBy(spaces, (s) => s.name.toLowerCase()), [spaces]);
    const missing = typesMissingQueries(considerationTypes).filter((type) => !isDraftPending(type));
    const draftingCount = considerationTypes.filter(isDraftPending).length;
    const flagsLoading = queryFilter !== 'all' && !queryFlags;

    const filteredTypes = useMemo(() => {
        return considerationTypes.filter((t) => {
            if (statusFilter !== 'all' && t.documentStatus !== statusFilter) return false;
            if (spaceFilter !== 'all' && t.spaceId !== spaceFilter) return false;
            if (queryFilter !== 'all' && !queryFlags?.[t.id]?.includes(queryFilter)) return false;
            return true;
        });
    }, [considerationTypes, statusFilter, spaceFilter, queryFilter, queryFlags]);

    const sortedTypes = useMemo(
        () => orderBy(filteredTypes, [(t) => t.title.toLowerCase(), (t) => t.spaceId?.toLowerCase() ?? '']),
        [filteredTypes],
    );

    return (
        <PageShell>
            <PageHeader
                title="Considerations"
                description="Manage reusable consideration content that is linked to problem types."
            >
                <Select value={spaceFilter} onValueChange={(value) => setSpaceFilter(value as SpaceFilter)}>
                    <SelectTrigger className="w-[180px]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Spaces</SelectItem>
                        {sortedSpaces.map((space) => (
                            <SelectItem key={space.id} value={space.id}>
                                {space.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select
                    value={queryFilter}
                    onValueChange={(value) => setSearchParam('query', value === 'all' ? undefined : value)}
                >
                    <SelectTrigger className="w-[280px]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Any query status</SelectItem>
                        {QUERY_FLAGS.map((flag) => (
                            <SelectItem key={flag} value={flag}>
                                {QUERY_FLAG_LABELS[flag]}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as StatusFilter)}>
                    <SelectTrigger className="w-[160px]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {DocumentStatusSchema.options.map((status) => (
                            <SelectItem key={status} value={status}>
                                {STATUS_FILTER_LABELS[status]}
                            </SelectItem>
                        ))}
                        <SelectItem value="all">{STATUS_FILTER_LABELS.all}</SelectItem>
                    </SelectContent>
                </Select>
                <Button variant="outline" disabled={busy || missing.length === 0} onClick={() => draftMissing(missing)}>
                    {missing.length > 0
                        ? `Draft ${missing.length} missing queries`
                        : draftingCount > 0
                          ? `Drafting ${draftingCount}…`
                          : 'No missing queries'}
                </Button>
            </PageHeader>

            {flagsLoading ? (
                <Card>
                    <CardContent className="p-6 text-muted-foreground">Checking queries…</CardContent>
                </Card>
            ) : sortedTypes.length === 0 ? (
                <Card>
                    <CardHeader>
                        <CardTitle>No considerations found</CardTitle>
                        <CardDescription>
                            {statusFilter === 'all' && spaceFilter === 'all' && queryFilter === 'all'
                                ? 'Considerations will appear here once they are synced from Notion.'
                                : 'No considerations match the current filters. Try a different filter.'}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="section-description">
                            Create a consideration doc in Notion and sync it to create a consideration type.
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <Card>
                    <CardHeader>
                        <CardTitle>Considerations</CardTitle>
                        <CardDescription>Tap a row to review the consideration details and history.</CardDescription>
                    </CardHeader>
                    <CardContent className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="min-w-[200px]">Title</TableHead>
                                    <TableHead className="min-w-[160px]">Space ID</TableHead>
                                    <TableHead className="min-w-[100px]">Status</TableHead>
                                    <TableHead className="min-w-[240px]">Query</TableHead>
                                    <TableHead className="min-w-[100px]">Tasks</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sortedTypes.map((type) => (
                                    <ConsiderationTypeRow
                                        key={type.id}
                                        type={type}
                                        flags={queryFlags?.[type.id]}
                                        taskCount={taskTypesByType?.[type.id]?.length ?? 0}
                                        onClick={onRowClick(
                                            `/content/considerations/${encodeURIComponent(type.id)}/details`,
                                        )}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}
        </PageShell>
    );
};

const ConsiderationTypeRow: FC<{
    type: ConsiderationType;
    flags: QueryFlag[] | undefined;
    taskCount: number;
    onClick: (event: MouseEvent) => void;
}> = ({ type, flags, taskCount, onClick }) => (
    <TableRow className="cursor-pointer transition-colors hover:bg-muted/50" onClick={onClick}>
        <TableCell className="font-medium">{type.title}</TableCell>
        <TableCell>{type.spaceId || '-'}</TableCell>
        <TableCell>
            <DocumentStatusBadge status={type.documentStatus} />
        </TableCell>
        <TableCell className="max-w-md whitespace-normal">
            {flags ? <QueryStatus query={type.appliesTo} flags={flags} /> : '…'}
        </TableCell>
        <TableCell>{taskCount}</TableCell>
    </TableRow>
);
